// Centrala kommersiella villkor – ENDA sanningskälla för bindning,
// uppsägning, leveranstider och prisankare. FEN-001, FEN-005, FEN-006.
//
// Reglar:
// - Hårdkoda aldrig dessa siffror på en sida – importera härifrån.
// - Vid ändring: uppdatera här och ev. answers.ts / faq-data.ts som
//   genererar AEO-svar. Inget annat.

import { PACKAGES } from "@/lib/pricing";

// ---- Bindning / uppsägning ---------------------------------------------

export type ProductTerms = {
  productId: "pulse" | "pilot" | "prime";
  /** Bindningstid i månader. null = ingen bindning (engångsprojekt). */
  commitmentMonths: number | null;
  /** Uppsägningstid i månader (avser löpande avtal). */
  cancellationNoticeMonths: number;
  /** Faktureringsperiod. */
  billingInterval: "monthly" | "quarterly" | "project";
  pauseAllowed: boolean;
  pauseTerms?: string;
  /** Kort, citerbar fras (1 mening). Använd i FAQ + AEO. */
  termsSummary: string;
};

export const PRODUCT_TERMS: Record<ProductTerms["productId"], ProductTerms> = {
  pulse: {
    productId: "pulse",
    commitmentMonths: null,
    cancellationNoticeMonths: 0,
    billingInterval: "monthly",
    pauseAllowed: true,
    pauseTerms: "Pausa eller byt plan när ni vill — inga extra avgifter.",
    termsSummary:
      "Pulse rullar månadsvis utan bindning — säg upp när ni vill.",
  },
  pilot: {
    productId: "pilot",
    commitmentMonths: null,
    cancellationNoticeMonths: 0,
    billingInterval: "project",
    pauseAllowed: false,
    termsSummary: "Pilot är en engångsproduktion utan bindning eller uppsägning.",
  },
  prime: {
    productId: "prime",
    commitmentMonths: null,
    cancellationNoticeMonths: 0,
    billingInterval: "project",
    pauseAllowed: false,
    termsSummary: "Prime är ett projektupplägg med fastpris per produktion – ingen bindning.",
  },
};

// ---- Leveranstider (5 faser, FEN-006) ----------------------------------

export type DeliveryPhases = {
  /** Tid till första möte (vardagar). */
  toFirstMeetingDays: number;
  /** Tid från startklart till första inspelningsdag. */
  toShootDays: number;
  /** Tid till första klipp efter inspelning. */
  toFirstCutDays: number;
  /** Tid till godkänd huvudleverans. */
  toFinalDeliveryDays: number;
  /** Tid till samtliga versioner / format. */
  toAllVersionsDays: number;
};

export const DELIVERY: Record<"pulse" | "pilot" | "prime", DeliveryPhases> = {
  pulse: {
    toFirstMeetingDays: 2,
    toShootDays: 10,
    toFirstCutDays: 14,
    toFinalDeliveryDays: 21,
    toAllVersionsDays: 28,
  },
  pilot: {
    toFirstMeetingDays: 2,
    toShootDays: 14,
    toFirstCutDays: 21,
    toFinalDeliveryDays: 28,
    toAllVersionsDays: 35,
  },
  prime: {
    toFirstMeetingDays: 3,
    toShootDays: 21,
    toFirstCutDays: 28,
    toFinalDeliveryDays: 42,
    toAllVersionsDays: 56,
  },
};

// ---- Citerbara prisankare (FEN-005) ------------------------------------
// Härleds från PACKAGES så texten inte kan drifta från kalkylatorn.

export const PRICE_ANCHORS = {
  pulseLite: PACKAGES.lite.priceMonthly, // 14 995
  pulseCore: PACKAGES.core.priceMonthly, // 34 995
  pulsePro: PACKAGES.pro.priceMonthly, // 45 995
  pilotFrom: 28_995, // engångs
  primeFrom: 75_000, // projektgolv – uppdatera vid förändring
  currency: "SEK" as const,
  vatIncluded: false,
} as const;

/** Format: "14 995 kr". */
export const formatPriceSEK = (n: number) =>
  `${new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 0 }).format(n)} kr`;

/** Citerbar prismening för Pulse-paketen. */
export const PULSE_PRICE_LINE = `Pulse Lite ${formatPriceSEK(PRICE_ANCHORS.pulseLite)}/mån, Core ${formatPriceSEK(PRICE_ANCHORS.pulseCore)}/mån, Pro ${formatPriceSEK(PRICE_ANCHORS.pulsePro)}/mån`;

/** "Första klippet inom X dagar" – härleds från DELIVERY.pulse. */
export const FIRST_CUT_LINE = `första klippet inom ${DELIVERY.pulse.toFirstCutDays} dagar`;

// ---- Full strukturell ProductTerms-modell (FEN-002, FEN-004) ----------
// Strukturen byggs ut nu; värden som kräver affärsbeslut markeras
// `pendingApproval: true` och refereras i docs/business-decisions-pending.md.

export type FullProductTerms = {
  productId: "pulse" | "pilot" | "prime";
  pricing: {
    startPriceSek: number;
    vat: "ex" | "inc";
    scope: string;
    effectiveFrom: string;
  };
  commitment: {
    minMonths: number | null;
    noticePeriodMonths: number;
    billingInterval: "monthly" | "quarterly" | "project";
    pauseRule: string;
  };
  delivery: {
    firstMeetingDays: [number, number];
    shootDays: [number, number];
    firstCutDays: [number, number];
    mainDeliveryDays: [number, number];
    allVersionsDays: [number, number];
    startCondition: string;
  };
  approvedAt: string | null;
  pendingApproval?: boolean;
};

export const FULL_TERMS: Record<FullProductTerms["productId"], FullProductTerms> = {
  pulse: {
    productId: "pulse",
    pricing: {
      startPriceSek: PRICE_ANCHORS.pulseLite,
      vat: "ex",
      scope: "Pulse Lite – startpris/månad, faktureras månadsvis.",
      effectiveFrom: "2026-01-01",
    },
    commitment: {
      minMonths: null,
      noticePeriodMonths: 0,
      billingInterval: "monthly",
      pauseRule: "Pausa eller byt plan när ni vill — inga extra avgifter.",
    },
    delivery: {
      firstMeetingDays: [1, DELIVERY.pulse.toFirstMeetingDays],
      shootDays: [7, DELIVERY.pulse.toShootDays],
      firstCutDays: [10, DELIVERY.pulse.toFirstCutDays],
      mainDeliveryDays: [14, DELIVERY.pulse.toFinalDeliveryDays],
      allVersionsDays: [21, DELIVERY.pulse.toAllVersionsDays],
      startCondition: "från godkänd brief",
    },
    approvedAt: null,
    pendingApproval: true,
  },
  pilot: {
    productId: "pilot",
    pricing: {
      startPriceSek: PRICE_ANCHORS.pilotFrom,
      vat: "ex",
      scope: "Pilot – engångsproduktion, från-pris.",
      effectiveFrom: "2026-01-01",
    },
    commitment: {
      minMonths: null,
      noticePeriodMonths: 0,
      billingInterval: "project",
      pauseRule: "Ej tillämpligt – engångsprojekt.",
    },
    delivery: {
      firstMeetingDays: [1, DELIVERY.pilot.toFirstMeetingDays],
      shootDays: [10, DELIVERY.pilot.toShootDays],
      firstCutDays: [14, DELIVERY.pilot.toFirstCutDays],
      mainDeliveryDays: [21, DELIVERY.pilot.toFinalDeliveryDays],
      allVersionsDays: [28, DELIVERY.pilot.toAllVersionsDays],
      startCondition: "från godkänd brief",
    },
    approvedAt: null,
    pendingApproval: true,
  },
  prime: {
    productId: "prime",
    pricing: {
      startPriceSek: PRICE_ANCHORS.primeFrom,
      vat: "ex",
      scope: "Prime – projektgolv per produktion.",
      effectiveFrom: "2026-01-01",
    },
    commitment: {
      minMonths: null,
      noticePeriodMonths: 0,
      billingInterval: "project",
      pauseRule: "Ej tillämpligt – projektupplägg.",
    },
    delivery: {
      firstMeetingDays: [2, DELIVERY.prime.toFirstMeetingDays],
      shootDays: [14, DELIVERY.prime.toShootDays],
      firstCutDays: [21, DELIVERY.prime.toFirstCutDays],
      mainDeliveryDays: [28, DELIVERY.prime.toFinalDeliveryDays],
      allVersionsDays: [42, DELIVERY.prime.toAllVersionsDays],
      startCondition: "från godkänd brief",
    },
    approvedAt: null,
    pendingApproval: true,
  },
};
